import React, { useEffect, useState } from 'react';
import './api-keys.scss';
import { ReactComponent as AddCircle } from 'design_system/src/static/icons/add-circle.svg';
import {
	Button,
	Input,
	ListTable,
	ModalConfirmation,
	Typography,
} from 'design_system/src';
import { toasterNotification } from 'models/shared/helpers/toasterNotification';
import { toasterOptions } from 'models/shared/enums/toaster.enum';
import { extractErrorMessage } from 'models/shared/helpers/errors';
import { fetchCompanies } from 'models/user-management/controllers/companies.controller';
import { Company } from 'models/companies/interfaces/companies.interface';
import {
	ApiKey,
	ApiKeyStatus,
} from 'models/api-keys/interfaces/api-keys.interface';
import {
	fetchApiKeys,
	revokeApiKey,
} from 'models/api-keys/controllers/api-keys.controller';
import CreateApiKey from './create/CreateApiKey';

type BaseTypographyProps = { children?: React.ReactNode; className?: string };

function ApiKeys() {
	const [companies, setCompanies] = useState<Company[]>([]);
	const [selectedCompanyId, setSelectedCompanyId] = useState('');
	const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
	const [loading, setLoading] = useState(false);
	const [openCreateModal, setOpenCreateModal] = useState(false);
	const [openRevokeModal, setOpenRevokeModal] = useState(false);
	const [revokeTarget, setRevokeTarget] = useState<ApiKey | null>(null);

	useEffect(() => {
		fetchCompanies()
			.then((data) => setCompanies(Array.isArray(data) ? data : []))
			.catch(() => setCompanies([]));
	}, []);

	const loadApiKeys = async () => {
		if (!selectedCompanyId) {
			setApiKeys([]);
			return;
		}

		setLoading(true);
		try {
			const data = await fetchApiKeys(selectedCompanyId);
			setApiKeys(Array.isArray(data) ? data : []);
		} catch (error: unknown) {
			toasterNotification({
				msg: extractErrorMessage(error) || 'Error loading api keys',
				toasterType: toasterOptions.error,
			});
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		loadApiKeys();
	}, [selectedCompanyId]);

	const handleCreate = () => {
		setOpenCreateModal(true);
	};

	const handleCloseCreateModal = () => {
		setOpenCreateModal(false);
	};

	const handleSaveSuccess = () => {
		loadApiKeys();
		handleCloseCreateModal();
	};

	const handleAskRevoke = (apiKey: ApiKey) => {
		setRevokeTarget(apiKey);
		setOpenRevokeModal(true);
	};

	const handleConfirmRevoke = async () => {
		if (!revokeTarget) return;

		try {
			await revokeApiKey(selectedCompanyId, revokeTarget._id);
			toasterNotification({
				msg: 'API key revoked successfully',
				toasterType: toasterOptions.success,
			});
			setOpenRevokeModal(false);
			setRevokeTarget(null);
			loadApiKeys();
		} catch (error: unknown) {
			toasterNotification({
				msg: extractErrorMessage(error) || 'Error revoking api key',
				toasterType: toasterOptions.error,
			});
		}
	};

	const cols = [
		{ label: 'Label', attribute: 'label' as keyof ApiKey },
		{
			label: 'Key',
			render: (
				row: ApiKey,
				_index: number,
				BaseTypography: React.FC<BaseTypographyProps>,
			) => <BaseTypography>{row.keyPrefix}••••••••</BaseTypography>,
		},
		{
			label: 'Status',
			render: (
				row: ApiKey,
				_index: number,
				BaseTypography: React.FC<BaseTypographyProps>,
			) => (
				<BaseTypography>
					{row.status === ApiKeyStatus.ACTIVE ? 'Active' : 'Revoked'}
				</BaseTypography>
			),
		},
		{
			label: 'Last used',
			render: (
				row: ApiKey,
				_index: number,
				BaseTypography: React.FC<BaseTypographyProps>,
			) => (
				<BaseTypography>
					{row.lastUsedAt ? new Date(row.lastUsedAt).toLocaleString() : 'Never'}
				</BaseTypography>
			),
		},
		{
			label: 'Created',
			render: (
				row: ApiKey,
				_index: number,
				BaseTypography: React.FC<BaseTypographyProps>,
			) => (
				<BaseTypography>
					{new Date(row.createdAt).toLocaleDateString()}
				</BaseTypography>
			),
		},
		{
			label: 'Actions',
			render: (row: ApiKey) =>
				row.status === ApiKeyStatus.ACTIVE ? (
					<Button
						variant="outline"
						scale="mini"
						color="red"
						onClick={() => handleAskRevoke(row)}
					>
						<Typography scale="xsmall" weight="400">
							Revoke
						</Typography>
					</Button>
				) : (
					<span />
				),
		},
	];

	return (
		<div className="api-keys-container">
			<div className="api-keys-header">
				<Typography scale="heading3" weight="600">
					API Keys
				</Typography>
				<Button
					variant="principal"
					icon={<AddCircle />}
					onClick={handleCreate}
					disabled={!selectedCompanyId}
				>
					Create API Key
				</Button>
			</div>

			<div style={{ maxWidth: '320px', marginBottom: '24px' }}>
				<Input
					name="companyId"
					type="select"
					title="Company"
					value={selectedCompanyId}
					onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
						setSelectedCompanyId(e.target.value)
					}
					options={companies.map((c) => ({ value: c._id, label: c.name }))}
				/>
			</div>

			{loading ? (
				<div style={{ padding: 24, textAlign: 'center' }}>
					<Typography scale="medium" weight="400">
						Loading api keys...
					</Typography>
				</div>
			) : (
				<ListTable
					selectId="_id"
					canSelect={false}
					data={apiKeys}
					cols={cols}
				/>
			)}

			{openCreateModal && (
				<CreateApiKey
					companyId={selectedCompanyId}
					onClose={handleCloseCreateModal}
					onSave={handleSaveSuccess}
				/>
			)}

			<ModalConfirmation
				openConfirmation={openRevokeModal}
				handleClose={() => {
					setOpenRevokeModal(false);
					setRevokeTarget(null);
				}}
				onConfirmationCallBack={handleConfirmRevoke}
				copyConfirm="Yes"
				copyReject="No"
				widthDesktop={30}
				title={
					<Typography scale="heading4" weight="600" className="text_center">
						Confirm revoke
					</Typography>
				}
				subtitle={
					<Typography scale="small" weight="400" className="text_center">
						{revokeTarget
							? `Revoke the api key "${revokeTarget.label}"? Any integration using it will stop working immediately.`
							: ''}
					</Typography>
				}
			/>
		</div>
	);
}

export default ApiKeys;
